/**
 * Bayes' theorem for one hypothesis A and one piece of evidence B:
 *
 *   P(A|B) = P(B|A) · P(A) / P(B),  P(B) = P(B|A) · P(A) + P(B|¬A) · (1 − P(A)).
 *
 * In the medical-test reading, P(A) is the prevalence, P(B|A) the sensitivity,
 * P(B|¬A) the false-positive rate (1 − specificity), and P(A|B) the positive predictive
 * value.
 */

export interface BayesResult {
  prior: number;
  /** P(B|A). */
  likelihood: number;
  /** P(B|¬A). */
  falsePositiveRate: number;
  /** P(B). */
  evidence: number;
  /** P(A|B). */
  posterior: number;
  /** P(A|¬B); null when B is certain. */
  posteriorGivenNotB: number | null;
  /** P(¬A|¬B), the negative predictive value; null when B is certain. */
  notAGivenNotB: number | null;
}

function isProbability(value: number): boolean {
  return value >= 0 && value <= 1;
}

function complete(
  prior: number,
  likelihood: number,
  falsePositiveRate: number
): BayesResult | null {
  const evidence = likelihood * prior + falsePositiveRate * (1 - prior);
  if (!(evidence > 0)) {
    return null;
  }
  const notEvidence = 1 - evidence;
  const posteriorGivenNotB =
    notEvidence > 0 ? ((1 - likelihood) * prior) / notEvidence : null;
  return {
    prior,
    likelihood,
    falsePositiveRate,
    evidence,
    posterior: (likelihood * prior) / evidence,
    posteriorGivenNotB,
    notAGivenNotB: posteriorGivenNotB === null ? null : 1 - posteriorGivenNotB,
  };
}

/** From P(A), P(B|A) and P(B|¬A). Null when P(B) would be zero. */
export function bayesFromRates(
  prior: number,
  likelihood: number,
  falsePositiveRate: number
): BayesResult | null {
  if (
    !(
      isProbability(prior) &&
      isProbability(likelihood) &&
      isProbability(falsePositiveRate)
    )
  ) {
    return null;
  }
  return complete(prior, likelihood, falsePositiveRate);
}

export type MarginalError = "evidence-too-small" | "evidence-too-large";

/** The range of P(B) consistent with P(A) and P(B|A). */
export function evidenceBounds(
  prior: number,
  likelihood: number
): { low: number; high: number } {
  const low = likelihood * prior;
  return { low, high: low + (1 - prior) };
}

/**
 * From P(A), P(B|A) and P(B). P(B|¬A) is implied and must lie in 0–1, which bounds P(B)
 * to [P(B|A)·P(A), P(B|A)·P(A) + 1 − P(A)].
 */
export function bayesFromEvidence(
  prior: number,
  likelihood: number,
  evidence: number
): BayesResult | MarginalError | null {
  if (
    !(
      isProbability(prior) &&
      isProbability(likelihood) &&
      isProbability(evidence)
    ) ||
    prior >= 1
  ) {
    return null;
  }
  const { low, high } = evidenceBounds(prior, likelihood);
  const tolerance = 1e-12;
  if (evidence < low - tolerance) {
    return "evidence-too-small";
  }
  if (evidence > high + tolerance) {
    return "evidence-too-large";
  }
  const implied = (evidence - low) / (1 - prior);
  return complete(prior, likelihood, Math.min(1, Math.max(0, implied)));
}

export interface NaturalFrequencies {
  total: number;
  /** A and B: true positives. */
  aAndB: number;
  /** A and not B: false negatives. */
  aAndNotB: number;
  /** Not A and B: false positives. */
  notAAndB: number;
  /** Not A and not B: true negatives. */
  notAAndNotB: number;
  a: number;
  notA: number;
  b: number;
  notB: number;
}

/** The same probabilities as expected counts in a population of `total`. */
export function naturalFrequencies(
  result: BayesResult,
  total: number
): NaturalFrequencies {
  const a = total * result.prior;
  const notA = total - a;
  const aAndB = a * result.likelihood;
  const notAAndB = notA * result.falsePositiveRate;
  return {
    total,
    aAndB,
    aAndNotB: a - aAndB,
    notAAndB,
    notAAndNotB: notA - notAAndB,
    a,
    notA,
    b: aAndB + notAAndB,
    notB: total - aAndB - notAAndB,
  };
}
